import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { ToolSelect, type ToolOption } from "@/shared/components/ui/tool-select";
import { AlertTriangle, Check, CheckCircle2, ChevronsUpDown, Loader2, MinusCircle } from "lucide-react";
import { aprovado, type FieldCheck } from "@/shared/types/aiModels";
import { isModelless } from "@/shared/utils/modelCatalog";

/**
 * Escolha de provedor + modelo para UMA finalidade de IA.
 *
 * Nasceu dentro da tela de AI Models da organização, que tem duas finalidades. A plataforma
 * tem três (AB#85605), e copiar o par de campos seria copiar junto o veredito do teste, o
 * aviso de modelo fora do catálogo e as mensagens de falha — quatro coisas que precisam
 * continuar dizendo a mesma coisa nas duas telas.
 */

/**
 * Escolha de modelo, fechada no que o cadastro declara.
 *
 * Nao aceita texto livre: modelo digitado a mao chega ao gateway sem rota, sem api-version,
 * sem limites de contexto e sem preco, e cada uma dessas ausencias vira um default silencioso.
 * Onde a instalacao nomeia o proprio deployment, esse nome e declarado no formulario da
 * integracao — a escolha aqui continua sendo entre coisas que o cadastro descreve (AB#85434).
 */
export function ModelCombobox({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  emptyLabel,
  searchLabel,
  status,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  emptyLabel: string;
  searchLabel: string;
  status?: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled || options.length === 0}
        >
          {value ? (
            <span className="truncate font-mono text-xs">{value}</span>
          ) : (
            <span className="text-muted-foreground">
              {options.length === 0 ? emptyLabel : placeholder}
            </span>
          )}
          <span className="ml-2 flex shrink-0 items-center gap-2">
            {status}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchLabel} />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option}
                  value={option}
                  onSelect={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${option === value ? "opacity-100" : "opacity-0"}`}
                  />
                  <span className="font-mono text-xs">{option}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** O veredito dentro do próprio campo: é ali que a pessoa olha ao escolher. */
export function StatusIcon({ check }: { check: FieldCheck }) {
  if (check.state === "checking")
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  if (check.state === "idle") return null;
  if (aprovado(check)) return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (check.state === "done" && check.outcome.status === "skipped")
    return <MinusCircle className="h-4 w-4 text-muted-foreground" />;
  return <AlertTriangle className="h-4 w-4 text-destructive" />;
}

/**
 * A mensagem abaixo do campo, só quando há o que dizer.
 *
 * Ela nomeia a rota usada E a procedência dela: na tela da organização quem lê é o admin e
 * quem conserta o cadastro é o superadmin, e sem as duas coisas o admin não tem o que pedir.
 */
export function CheckMessage({ check }: { check: FieldCheck }) {
  const { t } = useTranslation();

  if (check.state === "idle" || check.state === "checking" || aprovado(check)) return null;

  if (check.state === "unreachable")
    return <p className="text-xs text-muted-foreground">{t("settings.models.test.unreachable")}</p>;

  if (check.state === "unavailable")
    return (
      <p className="text-xs text-muted-foreground">
        {t(`settings.models.test.unavailable.${check.reason}`)}
      </p>
    );

  const outcome = check.outcome;
  if (outcome.status === "skipped")
    return <p className="text-xs text-muted-foreground">{t("settings.models.test.skipped")}</p>;
  if (outcome.status === "ok") return null;

  // `provider_unavailable` nao acusa o cadastro: o provedor nao respondeu, e mandar o
  // admin procurar o superadmin por isso desperdicaria o tempo dos dois.
  // O mesmo vale para a busca web com o engine ou o provedor de busca fora do ar (AB#86885).
  const blamesRegistry = !["provider_unavailable", "engine_unavailable", "provider_failed"].includes(
    outcome.reason
  );

  return (
    <div className="flex items-start gap-1.5 text-xs text-destructive">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        {t("settings.models.test.failed", { route: outcome.route })}
        <span className="text-muted-foreground">
          {" "}
          — {t(`settings.models.test.routeFrom.${outcome.route_source}`)}
        </span>
        <span className="block text-muted-foreground">
          {t(`settings.models.test.reason.${outcome.reason}`)}
          {blamesRegistry ? ` ${t("settings.models.test.askSuperadmin")}` : ""}
        </span>
        {/* A explicacao que o provedor (ou o gateway) devolveu. Sem ela a tela descreve a
            CATEGORIA da falha e esconde a frase que resolve a duvida — foi um
            `embed is not supported by openrouter` virando "tente novamente" que motivou
            o AB#85434. Ja vem saneada da api. */}
        {outcome.detail ? (
          <span className="mt-1 block break-words font-mono text-[11px] text-muted-foreground">
            {outcome.detail}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * Valor gravado que o cadastro atual não declara — sobra de quando a tela aceitava texto
 * livre, ou de uma troca de provedor. A escolha já não permite chegar nesse estado, mas ele
 * existe em dado antigo, e some da lista sem aviso se a tela não disser nada.
 */
export function OffCatalogWarning({ value, options }: { value: string; options: string[] }) {
  const { t } = useTranslation();
  if (!value || options.length === 0 || options.includes(value)) return null;
  return (
    <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{t("settings.models.offCatalog")}</span>
    </p>
  );
}

export function ModelPurposeField({
  providerLabel,
  modelLabel,
  modelHelp,
  modelPlaceholder,
  providers,
  providerId,
  onProviderChange,
  model,
  onModelChange,
  models,
  check,
  loadingProviders,
}: {
  providerLabel: string;
  modelLabel: string;
  modelHelp?: string;
  modelPlaceholder?: string;
  providers: ToolOption[];
  providerId: string | null;
  onProviderChange: (id: string | null) => void;
  model: string;
  onModelChange: (model: string) => void;
  models: string[];
  check: FieldCheck;
  loadingProviders?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <>
      <div className="space-y-2">
        <label className="text-sm font-medium">{providerLabel}</label>
        <ToolSelect
          value={providerId}
          onValueChange={onProviderChange}
          options={providers}
          placeholder={t("settings.organization.selectModel")}
          disabled={loadingProviders}
          loading={loadingProviders}
          allowEmpty={true}
          emptyLabel={t("settings.organization.noModel")}
          className="w-full"
        />
      </div>
      {isModelless(models) ? (
        // Provedor sem modelo (Tavily, na busca web): o campo some, e o veredito do teste
        // continua à vista — é ele que diz se a credencial funciona (AB#86885).
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <StatusIcon check={check} />
            {t("settings.models.modelless")}
          </p>
          <CheckMessage check={check} />
        </div>
      ) : (
        <div className="space-y-2">
          <label className="text-sm font-medium">{modelLabel}</label>
          <ModelCombobox
            value={model}
            onChange={onModelChange}
            options={models}
            placeholder={modelPlaceholder}
            disabled={!providerId}
            emptyLabel={t("settings.models.noDeclaredModels")}
            searchLabel={t("settings.models.searchModels")}
            status={<StatusIcon check={check} />}
          />
          {modelHelp ? <p className="text-xs text-muted-foreground">{modelHelp}</p> : null}
          <OffCatalogWarning value={model} options={models} />
          <CheckMessage check={check} />
        </div>
      )}
    </>
  );
}
